import {
  Dialog,
  dialogButton,
  dialogField,
  dialogSegment,
  dialogSummaryRow,
  textInput,
} from './Dialog';
import { clear, el } from './dom';
import { icon } from './Icons';
import { keyCaps } from './ToolHint';
import {
  ABOUT_BLURB,
  aboutRows,
  describeQueue,
  keyReference,
  type AboutFacts,
} from './About';
import {
  FEEDBACK_TOPICS,
  feedbackPrompt,
  feedbackTitle,
  formatFeedback,
  localFeedbackChannel,
  readFeedbackQueue,
  submitFeedback,
  type FeedbackChannel,
  type FeedbackTopic,
} from './Feedback';

/**
 * **Leave feedback** and **ⓘ** — the pair at the top right of the toolbar.
 *
 * ## Why they are a pair, and why they are there
 *
 * Every other control in the toolbar acts on the garden. These two act on the *program*: one says
 * what it is, the other says what is wrong with it. Top right is where a user has been trained by
 * every application they have ever used to look for exactly that, which is the whole argument for
 * the position — they sit after the spacer, beside the account control, away from the tools.
 *
 * They are one component rather than two because they are two halves of one errand. Somebody about
 * to report a problem wants the version number; somebody reading the version number is usually
 * halfway to reporting a problem. So the About dialog carries a button into the feedback form, and
 * the feedback form quotes the same build the About dialog does — from one place, once.
 *
 * ## The report gathers its own evidence, in the open
 *
 * A useful bug report names the build, the catalog, the site, and the browser. Asking a designer to
 * type those is asking for a worse report; attaching them quietly is taking something that was not
 * offered. So the form shows exactly what it will send, in a disclosure the sender can open before
 * pressing anything, and sends nothing else. See {@link Feedback.FeedbackDraft.context}.
 *
 * ## It reports what actually happened
 *
 * The shipped channel queues to this browser and copies to the clipboard — nothing is delivered
 * anywhere (see `Feedback.ts`). Rather than flash that past in a toast, the form replaces itself
 * with the channel's own account of what it did and holds it until the sender dismisses it. A
 * message that says "nothing has been sent" is the one message worth making sure was read.
 */

export interface HelpMenuDeps {
  /** Where the dialogs mount — the HUD root, so they inherit its theme and leave with it. */
  host: HTMLElement;
  /** Read at open time, not construction: the site, its plants and its place all change underneath. */
  facts: () => AboutFacts;
  /**
   * Session facts to attach to a report, one per line. The HUD supplies what it knows about the
   * garden; {@link runtimeContext} adds what the browser knows about itself.
   */
  context?: () => readonly string[];
  /** Where reports go. Defaults to the local queue. */
  channel?: FeedbackChannel;
  /**
   * Reopen the guided tour. Absent on a host that has none, in which case the offer is not made —
   * an About sheet advertising a tutorial that does not open is worse than one that never mentions
   * it. See `TourOverlay.ts`.
   */
  onTour?: () => void;
}

export class HelpMenu {
  readonly root: HTMLElement;

  private readonly channel: FeedbackChannel;
  private dialog: Dialog | null = null;

  constructor(private readonly deps: HelpMenuDeps) {
    this.channel = deps.channel ?? localFeedbackChannel();

    this.root = el(
      'div',
      { class: 'lg-help' },
      el(
        'button',
        {
          class: 'lg-iconbtn lg-help__feedback',
          title: 'Leave feedback',
          attrs: { type: 'button', 'aria-label': 'Leave feedback' },
          on: { click: () => this.openFeedback() },
        },
        el('span', { class: 'lg-help__icon', html: icon('feedback', 15) }),
        el('span', { class: 'lg-help__label', text: 'Feedback' }),
      ),
      el('button', {
        class: 'lg-iconbtn lg-help__info',
        html: icon('info', 16),
        title: 'About Lush Glade — build, catalog, and the keyboard',
        attrs: { type: 'button', 'aria-label': 'About Lush Glade' },
        on: { click: () => this.openAbout() },
      }),
    );
  }

  /* --------------------------------- Leave feedback --------------------------------- */

  /** Open the report form. Also reached from the About dialog. */
  openFeedback(): void {
    this.dialog?.dispose();

    const facts = this.deps.facts();
    const context = [...(this.deps.context?.() ?? []), ...runtimeContext()];
    let topic: FeedbackTopic | null = null;

    const message = el('textarea', {
      class: 'lg-input lg-help__message',
      attrs: {
        rows: '5',
        placeholder: feedbackPrompt({ scope: 'app', message: '' }),
        spellcheck: 'true',
        'aria-label': 'Your feedback',
      },
    });
    const contact = textInput({
      placeholder: 'Email, if you would like a reply (optional)',
      ariaLabel: 'Contact',
    });
    const status = el('p', { class: 'lg-legend lg-help__status' });

    // Rebuilt rather than restyled on each pick: the segment renders its own pressed state at build
    // time, and the question in the message box below it changes with the answer.
    const topicSlot = el('div', { class: 'lg-help__topics' });
    const renderTopics = (): void => {
      clear(topicSlot);
      topicSlot.appendChild(
        dialogSegment(
          'What kind of feedback',
          FEEDBACK_TOPICS.map((entry) => ({ id: entry.id, label: entry.label })),
          topic ?? '',
          (id) => {
            topic = id as FeedbackTopic;
            message.placeholder = feedbackPrompt({ scope: 'app', topic, message: '' });
            renderTopics();
            message.focus();
          },
        ),
      );
    };
    renderTopics();

    const dialog = new Dialog({
      host: this.deps.host,
      title: feedbackTitle({ scope: 'app', message: '' }),
      subtitle: 'Tell us what is wrong, what is missing, or what worked.',
      icon: 'feedback',
      onClose: () => {
        this.dialog = null;
      },
    });
    this.dialog = dialog;

    dialog.body.appendChild(topicSlot);
    dialog.body.appendChild(dialogField('Your feedback', message));
    dialog.body.appendChild(dialogField('Contact', contact));
    dialog.body.appendChild(contextDisclosure(context));
    dialog.body.appendChild(el('p', { class: 'lg-hint', text: this.channel.describe }));
    dialog.body.appendChild(status);

    dialog.setActions(
      dialogButton({ label: 'Cancel', onClick: () => dialog.close() }),
      dialogButton({
        label: 'Send feedback',
        primary: true,
        icon: 'feedback',
        onClick: () => {
          if (message.value.trim() === '') {
            status.textContent = 'Say what happened — that is the part nobody else can supply.';
            status.classList.add('is-error');
            message.focus();
            return;
          }
          void submitFeedback(
            this.channel,
            {
              scope: 'app',
              message: message.value.trim(),
              context,
              ...(topic ? { topic } : {}),
              ...(contact.value.trim() ? { contact: contact.value.trim() } : {}),
            },
            { catalogVersion: facts.catalogVersion, appVersion: facts.appVersion },
          ).then((result) => {
            this.reportOutcome(dialog, result.ok, result.message);
          });
        },
      }),
    );

    dialog.show();
    message.focus();
  }

  /** Replace the form with what the channel actually did, and hold it until dismissed. */
  private reportOutcome(dialog: Dialog, ok: boolean, outcome: string): void {
    clear(dialog.body);
    dialog.setSubtitle(ok ? 'Thank you — that is genuinely useful.' : 'It did not get through.');
    dialog.body.appendChild(
      el('p', {
        class: ok ? 'lg-hint' : 'lg-hint is-error',
        text: outcome,
      }),
    );
    if (ok) {
      dialog.body.appendChild(
        el('p', {
          class: 'lg-hint',
          text:
            'Nothing leaves this machine on its own yet. What is on your clipboard is the whole ' +
            'report — paste it wherever you would send it.',
        }),
      );
    }
    dialog.setActions(dialogButton({ label: 'Close', primary: true, onClick: () => dialog.close() }));
  }

  /* ------------------------------------- About ------------------------------------- */

  /** Open the info sheet: what this build is, what it is reading, and what the keyboard does. */
  openAbout(): void {
    this.dialog?.dispose();

    const facts = this.deps.facts();
    const queue = readFeedbackQueue();

    const dialog = new Dialog({
      host: this.deps.host,
      title: 'Lush Glade',
      subtitle: `Version ${facts.appVersion}`,
      icon: 'info',
      onClose: () => {
        this.dialog = null;
      },
    });
    this.dialog = dialog;

    dialog.body.appendChild(el('p', { class: 'lg-hint', text: ABOUT_BLURB }));

    const summary = el('div', { class: 'lg-dialog__summary' });
    for (const row of aboutRows(facts)) {
      summary.appendChild(dialogSummaryRow(row.label, row.value));
    }
    dialog.body.appendChild(summary);

    // Above the keyboard reference rather than below it: someone who has opened this sheet looking
    // for help wants the tour before they want the list of shortcuts, and the list is long enough
    // that anything under it is effectively unfindable.
    if (this.deps.onTour) dialog.body.appendChild(this.tourSection(dialog));
    dialog.body.appendChild(this.queueSection(queue.length));
    dialog.body.appendChild(keyboardSection());

    dialog.setActions(
      dialogButton({ label: 'Close', onClick: () => dialog.close() }),
      dialogButton({
        label: 'Leave feedback',
        primary: true,
        icon: 'feedback',
        onClick: () => {
          dialog.close();
          this.openFeedback();
        },
      }),
    );

    dialog.show();
  }

  /** The way back into the tutorial, for anyone who skipped it or wants it again. */
  private tourSection(dialog: Dialog): HTMLElement {
    return el(
      'div',
      { class: 'lg-help__tour' },
      el('p', {
        class: 'lg-hint',
        text: 'A two-minute pass over the main features, pointing at each one on screen.',
      }),
      dialogButton({
        label: 'Take the tour',
        icon: 'sparkle',
        onClick: () => {
          // Shut first: the tour spotlights the toolbar this sheet is covering.
          dialog.close();
          this.deps.onTour?.();
        },
      }),
    );
  }

  /**
   * What is waiting in this browser, and a way to get it out.
   *
   * The copy button is the whole reason this section exists: a queue nobody can drain is a drawer
   * with the reports locked in it, and until a real endpoint lands (`FeedbackChannel`) the clipboard
   * is the endpoint.
   */
  private queueSection(count: number): HTMLElement {
    const status = el('p', { class: 'lg-hint', text: describeQueue(count) });
    const section = el('div', { class: 'lg-help__queue' }, status);
    if (count === 0) return section;

    section.appendChild(
      dialogButton({
        label: count === 1 ? 'Copy the report' : `Copy all ${String(count)} reports`,
        icon: 'download',
        onClick: () => {
          const text = readFeedbackQueue().map(formatFeedback).join('\n\n----\n\n');
          const clipboard = globalThis.navigator?.clipboard;
          // Tested for rather than optional-chained, for the reason `Feedback.ts` sets out at
          // length: an optional call on a missing API resolves happily and would have this button
          // claim a copy it never made.
          if (typeof clipboard?.writeText !== 'function') {
            status.textContent = 'This browser will not give the page its clipboard.';
            status.classList.add('is-error');
            return;
          }
          void clipboard.writeText(text).then(
            () => {
              status.textContent = 'Copied. Paste them wherever you would send them.';
            },
            () => {
              status.textContent = 'The clipboard refused. Nothing was copied.';
              status.classList.add('is-error');
            },
          );
        },
      }),
    );
    return section;
  }

  dispose(): void {
    this.dialog?.dispose();
    this.dialog = null;
    this.root.remove();
  }
}

/** The keyboard reference, built from the map the editor actually dispatches on. */
function keyboardSection(): HTMLElement {
  const section = el(
    'div',
    { class: 'lg-help__keys' },
    el('h3', { class: 'lg-section__title', text: 'Keyboard' }),
  );
  for (const group of keyReference()) {
    const list = el('dl', { class: 'lg-help__keylist' });
    for (const row of group.rows) {
      list.appendChild(
        el(
          'div',
          { class: 'lg-help__keyrow' },
          el('dt', { class: 'lg-help__keycaps', html: keyCaps(row.keys) }),
          el('dd', { class: 'lg-help__keytext', text: row.description }),
        ),
      );
    }
    section.appendChild(el('h4', { class: 'lg-help__keygroup', text: group.title }));
    section.appendChild(list);
  }
  return section;
}

/**
 * Shut by default, and openable before anything is sent.
 *
 * A disclosure rather than a paragraph because the list is long and dull right up to the moment it
 * matters — and shut is not hidden: the summary says how many lines are in there, so nobody has to
 * open it to know something is attached.
 */
function contextDisclosure(context: readonly string[]): HTMLElement {
  const list = el('ul', { class: 'lg-help__contextlist' });
  for (const line of context) list.appendChild(el('li', { text: line }));
  return el(
    'details',
    { class: 'lg-help__context' },
    el('summary', {
      text: `Sent with this report — ${String(context.length)} line${context.length === 1 ? '' : 's'}`,
    }),
    list,
  );
}

/**
 * What the browser knows about itself, for the reports where that is the answer.
 *
 * Deliberately short: the user agent, the window, and the pixel ratio account for most rendering
 * complaints, and anything more would be collecting for its own sake. Guarded because the HUD is
 * constructible headless.
 */
export function runtimeContext(): string[] {
  const lines: string[] = [];
  const agent = globalThis.navigator?.userAgent;
  if (agent) lines.push(`Browser: ${agent}`);
  const view = globalThis.window;
  if (view) {
    lines.push(
      `Window: ${String(Math.round(view.innerWidth))}×${String(Math.round(view.innerHeight))}` +
        ` at ${String(view.devicePixelRatio)}×`,
    );
  }
  return lines;
}
